import { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';

const API_BASE = process.env.REACT_APP_API_BASE || '/api';

export default function MathExamFlowBuilder() {
  const navigate = useNavigate();
  
  // Step 1: Basic Info
  const [examName, setExamName] = useState('');
  const [level, setLevel] = useState('3');
  const [availableChapters, setAvailableChapters] = useState([]);
  const [selectedChapters, setSelectedChapters] = useState([]); 
  
  // Step 2: Question Availability & Selection
  const [availability, setAvailability] = useState({}); 
  const [questionSelection, setQuestionSelection] = useState({}); 
  const [activeChapterTab, setActiveChapterTab] = useState(''); 
  
  // Step 3: Thresholds - ✅ REMOVED backup_fail (not used anywhere)
  const [thresholds, setThresholds] = useState({
    core_pass: 80,
    core_fail: 50,
    backup_pass: 80,
    supporting_basic_pass: 60,
    supporting_intermediate_pass: 60,
    supporting_advanced_pass: 60
  });
  
  const [currentStep, setCurrentStep] = useState(1);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');

  // Fetch available chapters when level changes
  useEffect(() => {
    fetchChapters();
  }, [level]);

  const fetchChapters = async () => {
    try {
      // Backend automatically filters for is_active = true
      const res = await fetch(`${API_BASE}/admin/math-chapters?level=${level}`);
      if (!res.ok) throw new Error('Failed to fetch chapters');
      const data = await res.json();
      setAvailableChapters(data.chapters || []);
    } catch (err) {
      console.error('❌ Failed to fetch chapters:', err);
    }
  };

  // Fetch question availability when chapter is selected
  const fetchAvailability = async () => {
    if (!level || selectedChapters.length === 0) return;
    
    setLoading(true);
    setError('');
    try {
      const newAvailability = {};
      const newSelection = {};

      for (const chapter of selectedChapters) {
        const res = await fetch(`${API_BASE}/admin/math-exam/question-availability?level=${level}&chapter_name=${encodeURIComponent(chapter)}`);
        if (!res.ok) throw new Error(`Failed to fetch availability for ${chapter}`);
        const data = await res.json();
        newAvailability[chapter] = data.availability;
        
        // Initialize selection structure
        const initialSelection = {
          core: { basic: {}, intermediate: {} },
          backup: { basic: {}, intermediate: {} },
          supporting: { basic: {}, intermediate: {}, advanced: {} }
        };
        
        for (const [conceptType, difficulties] of Object.entries(data.availability)) {
          for (const [difficulty, concepts] of Object.entries(difficulties)) {
            for (const concept of Object.keys(concepts)) {
              if (conceptType === 'core') {
                if (difficulty === 'basic' || difficulty === 'intermediate') {
                  initialSelection.core[difficulty][concept] = 1;
                  initialSelection.backup[difficulty][concept] = 1; // Default 1 backup question per core concept
                }
              } else if (conceptType === 'supporting') {
                initialSelection.supporting[difficulty][concept] = 1;
              }
            }
          }
        }
        newSelection[chapter] = initialSelection;
      }
      
      setAvailability(newAvailability);
      setQuestionSelection(newSelection);
      setActiveChapterTab(selectedChapters[0]); 
      setCurrentStep(2);
    } catch (err) {
      setError(err.message);
    } finally {
      setLoading(false);
    }
  };

  const handleQuestionCountChange = (chapter, conceptType, difficulty, concept, value) => {
    // Backup availability is based on the core availability for that exact difficulty
    let maxAvailable = 0;
    if (conceptType === 'backup') {
      maxAvailable = availability[chapter]?.core?.[difficulty]?.[concept] || 0;
    } else {
      maxAvailable = availability[chapter]?.[conceptType]?.[difficulty]?.[concept] || 0;
    }
    
    const count = Math.max(0, Math.min(parseInt(value) || 0, maxAvailable));
    
    setQuestionSelection(prev => ({
      ...prev,
      [chapter]: {
        ...prev[chapter],
        [conceptType]: {
          ...(prev[chapter][conceptType] || {}),
          [difficulty]: {
            ...(prev[chapter][conceptType]?.[difficulty] || {}),
            [concept]: count
          }
        }
      }
    }));
  };

  const getTotalSelected = () => {
    let total = 0;
    for (const chapter of selectedChapters) {
      if (!questionSelection[chapter]) continue;
      // Only count core and supporting for the primary exam total
      for (const conceptType of ['core', 'supporting']) {
        for (const difficulty of ['basic', 'intermediate', 'advanced']) {
          if (questionSelection[chapter]?.[conceptType]?.[difficulty]) {
            for (const count of Object.values(questionSelection[chapter][conceptType][difficulty])) {
              total += count;
            }
          }
        }
      }
    }
    return total;
  };

  const handleCreateExam = async () => {
    setLoading(true);
    setError('');

    try {
      if (!examName) throw new Error('Exam name is required');
      if (getTotalSelected() === 0) throw new Error('Please select at least one question');

      const chaptersPayload = selectedChapters.map(ch => ({
        chapter_name: ch,
        question_selection: questionSelection[ch]
      }));

      const payload = {
        exam_name: examName,
        level: level,
        chapters: chaptersPayload,
        thresholds: thresholds
      };

      const res = await fetch(`${API_BASE}/admin/math-exam`, {
        method: 'POST',
        headers: { 
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${localStorage.getItem('token')}`
        },
        body: JSON.stringify(payload)
      });

      const data = await res.json();
      if (!res.ok) throw new Error(data.error || 'Failed to create exam');

      alert(`✅ Exam "${examName}" created successfully!`);
      navigate('/admin/flow-builder-exams'); // Adjust route if your list page is named differently
    } catch (err) {
      console.error('❌ Create exam error:', err);
      setError(err.message);
    } finally {
      setLoading(false);
    }
  };

  return (
    <div style={{ maxWidth: 1200, margin: '40px auto', padding: 24 }}>
      {/* Header with Exit Button */}
      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 20 }}>
        <h2 style={{ margin: 0 }}>📐 Create Math Diagnostic Exam</h2>
        <button 
          onClick={() => navigate('/admin/flow-builder-exams')} 
          style={{ 
            padding: '10px 16px', 
            background: '#6c757d', 
            color: '#fff', 
            border: 'none', 
            borderRadius: 6, 
            cursor: 'pointer',
            fontWeight: 500,
            display: 'flex',
            alignItems: 'center',
            gap: 6
          }}
        >
          ✕ Cancel & Exit
        </button>
      </div>

      {error && <div style={{ padding: 12, background: '#f8d7da', color: '#721c24', borderRadius: 6, marginBottom: 15 }}>❌ {error}</div>}

      {/* Progress Indicator */}
      <div style={{ display: 'flex', gap: 10, marginBottom: 30 }}>
        {[1, 2, 3].map(step => (
          <div key={step} style={{
            flex: 1,
            padding: 12,
            background: currentStep >= step ? '#007BFF' : '#e9ecef',
            color: currentStep >= step ? '#fff' : '#666',
            borderRadius: 6,
            textAlign: 'center',
            fontWeight: 600
          }}>
            Step {step}: {step === 1 ? 'Basic Info' : step === 2 ? 'Question Selection' : 'Thresholds'}
          </div>
        ))}
      </div>

      {/* Step 1: Basic Info */}
      {currentStep === 1 && (
        <div style={{ background: '#fff', padding: 20, borderRadius: 8, border: '1px solid #ddd' }}>
          <h3 style={{ marginTop: 0 }}>📋 Basic Information</h3>
          
          <div style={{ marginBottom: 15 }}>
            <label style={{ display: 'block', marginBottom: 4, fontWeight: 500 }}>Exam Name</label>
            <input 
              type="text" 
              value={examName} 
              onChange={(e) => setExamName(e.target.value)}
              placeholder="e.g., Math Diagnostic - Level 3 - Numbers"
              style={{ width: '100%', padding: 10, borderRadius: 4, border: '1px solid #ccc', boxSizing: 'border-box' }}
            />
          </div>

          <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 15, marginBottom: 15 }}>
            <div>
              <label style={{ display: 'block', marginBottom: 4, fontWeight: 500 }}>Level</label>
              <select 
                value={level} 
                onChange={(e) => { setLevel(e.target.value); setSelectedChapters([]); }}
                style={{ width: '100%', padding: 10, borderRadius: 4, border: '1px solid #ccc', boxSizing: 'border-box' }}
              >
                {[1, 2, 3, 4, 5, 6, 7, 8, 9, 10, 11, 12].map(l => (
                  <option key={l} value={l}>Level {l}</option>
                ))}
              </select>
            </div>
          </div>

          <div style={{ marginBottom: 15 }}>
            <label style={{ display: 'block', marginBottom: 8, fontWeight: 500 }}>Select Chapters (You can select multiple)</label>
            {availableChapters.length === 0 ? (
              <p style={{ color: '#666', fontSize: 14 }}>No chapters available for this level. Please upload questions first.</p>
            ) : (
              <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fill, minmax(200px, 1fr))', gap: 10 }}>
                {availableChapters.map(ch => (
                  <label 
                    key={ch.id} 
                    style={{ 
                      display: 'flex', alignItems: 'center', gap: 8, padding: 12, 
                      background: selectedChapters.includes(ch.chapter_name) ? '#e7f1ff' : '#f8f9fa', 
                      border: `1px solid ${selectedChapters.includes(ch.chapter_name) ? '#007BFF' : '#ddd'}`, 
                      borderRadius: 6, cursor: 'pointer', transition: 'all 0.2s'
                    }}
                  >
                    <input 
                      type="checkbox" 
                      checked={selectedChapters.includes(ch.chapter_name)} 
                      onChange={(e) => {
                        if (e.target.checked) setSelectedChapters([...selectedChapters, ch.chapter_name]);
                        else setSelectedChapters(selectedChapters.filter(c => c !== ch.chapter_name));
                      }} 
                      style={{ width: 18, height: 18, cursor: 'pointer' }}
                    />
                    <span style={{ fontWeight: 500, fontSize: 14 }}>{ch.chapter_name}</span>
                  </label>
                ))}
              </div>
            )}
          </div>

          <button 
            onClick={fetchAvailability}
            disabled={!examName || selectedChapters.length === 0 || loading}
            style={{ 
              padding: '12px 24px', 
              background: (!examName || selectedChapters.length === 0) ? '#ccc' : '#007BFF', 
              color: '#fff', 
              border: 'none', 
              borderRadius: 6, 
              cursor: (!examName || selectedChapters.length === 0) ? 'not-allowed' : 'pointer',
              fontWeight: 600
            }}
          >
            {loading ? 'Loading...' : `Continue to Question Selection (${selectedChapters.length} chapters) →`}
          </button>
        </div>
      )}

      {/* Step 2: Question Selection */}
      {currentStep === 2 && Object.keys(availability).length > 0 && (
        <div style={{ background: '#fff', padding: 20, borderRadius: 8, border: '1px solid #ddd' }}>
          <h3 style={{ marginTop: 0 }}>📚 Question Selection by Concept</h3>
          <p style={{ color: '#666', marginBottom: 20 }}>
            Specify how many questions you want from each concept and difficulty level for each chapter.
          </p>

          {/* Chapter Tabs */}
          <div style={{ display: 'flex', gap: 8, marginBottom: 20, borderBottom: '2px solid #eee', flexWrap: 'wrap' }}>
            {selectedChapters.map(ch => (
              <button
                key={ch}
                onClick={() => setActiveChapterTab(ch)}
                style={{
                  padding: '10px 16px',
                  background: activeChapterTab === ch ? '#007BFF' : 'transparent',
                  color: activeChapterTab === ch ? '#fff' : '#333',
                  border: 'none',
                  borderBottom: activeChapterTab === ch ? '3px solid #0056b3' : '3px solid transparent',
                  cursor: 'pointer',
                  fontWeight: activeChapterTab === ch ? 600 : 400,
                  borderRadius: '6px 6px 0 0',
                  fontSize: 14
                }}
              >
                📚 {ch}
              </button>
            ))}
          </div>

          {/* Active Chapter Content */}
          {activeChapterTab && availability[activeChapterTab] && (
            <div>
              {['core', 'backup', 'supporting'].map(conceptType => (
                <div key={conceptType} style={{ marginBottom: 30 }}>
                  <h4 style={{ 
                    textTransform: 'capitalize',
                    color: conceptType === 'core' ? '#0056b3' : conceptType === 'backup' ? '#856404' : '#28a745',
                    borderBottom: `2px solid ${conceptType === 'core' ? '#0056b3' : conceptType === 'backup' ? '#856404' : '#28a745'}`,
                    paddingBottom: 8
                  }}>
                    {conceptType === 'backup' ? 'Backup Core Questions' : `${conceptType} Questions`}
                  </h4>

                  {conceptType === 'supporting' ? (
                    ['basic', 'intermediate', 'advanced'].map(difficulty => {
                      const concepts = availability[activeChapterTab][conceptType][difficulty];
                      if (!concepts || Object.keys(concepts).length === 0) return null;

                      return (
                        <div key={difficulty} style={{ marginBottom: 20, paddingLeft: 20 }}>
                          <h5 style={{ textTransform: 'capitalize', color: '#495057' }}>
                            {difficulty} ({Object.values(concepts).reduce((a, b) => a + b, 0)} available)
                          </h5>

                          <div style={{ display: 'grid', gap: 10 }}>
                            {Object.entries(concepts).map(([concept, count]) => (
                              <div key={concept} style={{ 
                                display: 'grid', 
                                gridTemplateColumns: '2fr 1fr 1fr', 
                                gap: 10, 
                                alignItems: 'center',
                                padding: 10,
                                background: '#f8f9fa',
                                borderRadius: 6
                              }}>
                                <div style={{ fontWeight: 500 }}>{concept}</div>
                                <div style={{ color: '#666', fontSize: 13 }}>Available: {count}</div>
                                <input 
                                  type="number"
                                  min="0"
                                  max={count}
                                  value={questionSelection[activeChapterTab]?.[conceptType]?.[difficulty]?.[concept] || 0}
                                  onChange={(e) => handleQuestionCountChange(activeChapterTab, conceptType, difficulty, concept, e.target.value)}
                                  style={{ padding: 8, borderRadius: 4, border: '1px solid #ccc', boxSizing: 'border-box' }}
                                />
                              </div>
                            ))}
                          </div>
                        </div>
                      );
                    })
                  ) : (
                    ['basic', 'intermediate'].map(difficulty => {
                      const concepts = availability[activeChapterTab]?.core?.[difficulty] || {};
                      if (Object.keys(concepts).length === 0) return null;

                      return (
                        <div key={difficulty} style={{ marginBottom: 20, paddingLeft: 20 }}>
                          <h5 style={{ textTransform: 'capitalize', color: '#495057' }}>
                            {difficulty} ({Object.values(concepts).reduce((a, b) => a + b, 0)} available)
                          </h5>

                          <div style={{ display: 'grid', gap: 10 }}>
                            {Object.entries(concepts).map(([concept, count]) => (
                              <div key={concept} style={{ 
                                display: 'grid', 
                                gridTemplateColumns: '2fr 1fr 1fr', 
                                gap: 10, 
                                alignItems: 'center',
                                padding: 10,
                                background: conceptType === 'backup' ? '#fff3cd' : '#f8f9fa',
                                borderRadius: 6
                              }}>
                                <div style={{ fontWeight: 500 }}>{concept}</div>
                                <div style={{ color: '#666', fontSize: 13 }}>Available: {count}</div>
                                <input 
                                  type="number"
                                  min="0"
                                  max={count}
                                  value={questionSelection[activeChapterTab]?.[conceptType]?.[difficulty]?.[concept] || 0}
                                  onChange={(e) => handleQuestionCountChange(activeChapterTab, conceptType, difficulty, concept, e.target.value)}
                                  style={{ padding: 8, borderRadius: 4, border: '1px solid #ccc', boxSizing: 'border-box' }}
                                />
                              </div>
                            ))}
                          </div>
                        </div>
                      );
                    })
                  )}
                </div>
              ))}
            </div>
          )}

          <div style={{ display: 'flex', gap: 10, marginTop: 20 }}>
            <button 
              onClick={() => setCurrentStep(1)}
              style={{ padding: '12px 24px', background: '#6c757d', color: '#fff', border: 'none', borderRadius: 6, cursor: 'pointer' }}
            >
              ← Back
            </button>
            <button 
              onClick={() => setCurrentStep(3)}
              disabled={getTotalSelected() === 0}
              style={{ 
                padding: '12px 24px', 
                background: getTotalSelected() === 0 ? '#ccc' : '#007BFF', 
                color: '#fff', 
                border: 'none', 
                borderRadius: 6, 
                cursor: getTotalSelected() === 0 ? 'not-allowed' : 'pointer',
                fontWeight: 600
              }}
            >
              Continue to Thresholds →
            </button>
          </div>

          <div style={{ marginTop: 15, padding: 10, background: '#e7f1ff', borderRadius: 6, fontSize: 13 }}>
            Total primary questions selected: <strong>{getTotalSelected()}</strong>
          </div>
        </div>
      )}

      {/* Step 3: Thresholds */}
      {currentStep === 3 && (
        <div style={{ background: '#fff', padding: 20, borderRadius: 8, border: '1px solid #ddd' }}>
          <h3 style={{ marginTop: 0 }}>🎯 Pass/Fail Thresholds & Exam Paths</h3>
          <p style={{ color: '#666', marginBottom: 20 }}>
            Set the minimum percentage required to pass each stage.
          </p>

          {/* Visual Path Diagram */}
          <div style={{ padding: 15, background: '#f8f9fa', borderRadius: 8, border: '1px solid #dee2e6', marginBottom: 20 }}>
            <h4 style={{ marginTop: 0, color: '#495057' }}>🗺️ Exam Progression Paths</h4>
            <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr 1fr', gap: 15, fontSize: 12 }}>
              <div style={{ padding: 10, background: '#f8d7da', borderRadius: 6, border: '1px solid #f5c6cb' }}>
                <strong style={{ color: '#721c24' }}>Fail Core</strong><br/>
                Score &lt; {thresholds.core_fail}%<br/>
                → Supporting Basic → <strong>End</strong>
              </div>
              <div style={{ padding: 10, background: '#fff3cd', borderRadius: 6, border: '1px solid #ffeeba' }}>
                <strong style={{ color: '#856404' }}>Threshold Core</strong><br/>
                Between {thresholds.core_fail}% - {thresholds.core_pass}%<br/>
                → Backup Core → <em>(See Backup Outcomes)</em>
              </div>
              <div style={{ padding: 10, background: '#d4edda', borderRadius: 6, border: '1px solid #c3e6cb' }}>
                <strong style={{ color: '#155724' }}>Pass Core</strong><br/>
                Score ≥ {thresholds.core_pass}%<br/>
                → Sup Int → Sup Adv → <strong>End</strong>
              </div>
            </div>
          </div>

          {/* Core Thresholds */}
          <div style={{ marginBottom: 30, padding: 15, background: '#f8f9fa', borderRadius: 8, border: '1px solid #dee2e6' }}>
            <h4 style={{ marginTop: 0, color: '#0056b3' }}>Core Assessment Thresholds</h4>
            <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 20 }}>
              <div style={{ padding: 15, background: '#d4edda', borderRadius: 6 }}>
                <label style={{ display: 'block', marginBottom: 8, fontWeight: 600, color: '#155724' }}>Core Pass Threshold (%)</label>
                <input 
                  type="number" min="0" max="100"
                  value={thresholds.core_pass}
                  onChange={(e) => setThresholds({...thresholds, core_pass: Number(e.target.value)})}
                  style={{ width: '100%', padding: 10, borderRadius: 4, border: '1px solid #ccc', boxSizing: 'border-box' }}
                />
                <small style={{ color: '#155724', display: 'block', marginTop: 4 }}>Score ≥ this → Supporting Intermediate</small>
              </div>
              <div style={{ padding: 15, background: '#f8d7da', borderRadius: 6 }}>
                <label style={{ display: 'block', marginBottom: 8, fontWeight: 600, color: '#721c24' }}>Core Fail Threshold (%)</label>
                <input 
                  type="number" min="0" max="100"
                  value={thresholds.core_fail}
                  onChange={(e) => setThresholds({...thresholds, core_fail: Number(e.target.value)})}
                  style={{ width: '100%', padding: 10, borderRadius: 4, border: '1px solid #ccc', boxSizing: 'border-box' }}
                />
                <small style={{ color: '#721c24', display: 'block', marginTop: 4 }}>Score &lt; this → Supporting Basic (End)</small>
              </div>
            </div>
          </div>

          {/* Backup Thresholds - ✅ UPDATED: Single pass threshold, fail is anything below */}
          <div style={{ marginBottom: 30, padding: 15, background: '#fff3cd', borderRadius: 8, border: '1px solid #ffeeba' }}>
            <h4 style={{ marginTop: 0, color: '#856404' }}>Backup Core Threshold</h4>
            <p style={{ fontSize: 13, color: '#856404', marginBottom: 15 }}>
              Triggered if Core score is between Fail and Pass thresholds. Backup is a simple pass/fail — any score below the pass threshold counts as a fail.
            </p>
            <div style={{ maxWidth: 420 }}>
              <div style={{ padding: 15, background: '#d4edda', borderRadius: 6 }}>
                <label style={{ display: 'block', marginBottom: 8, fontWeight: 600, color: '#155724' }}>Backup Pass Threshold (%)</label>
                <input 
                  type="number" min="0" max="100"
                  value={thresholds.backup_pass}
                  onChange={(e) => setThresholds({...thresholds, backup_pass: Number(e.target.value)})}
                  style={{ width: '100%', padding: 10, borderRadius: 4, border: '1px solid #ccc', boxSizing: 'border-box' }}
                />
                <small style={{ color: '#155724', display: 'block', marginTop: 4 }}>
                  ✓ Pass → Supporting Intermediate
                </small>
                <small style={{ color: '#721c24', display: 'block', marginTop: 4 }}>
                  ✗ Below threshold → Supporting Basic (End)
                </small>
              </div>
            </div>
          </div>

          {/* Supporting Thresholds */}
          <div style={{ marginBottom: 30, padding: 15, background: '#e2e3e5', borderRadius: 8, border: '1px solid #ced4da' }}>
            <h4 style={{ marginTop: 0, color: '#383d41' }}>Supporting Questions Pass Thresholds</h4>
            <p style={{ fontSize: 13, color: '#383d41', marginBottom: 15 }}>Pass = advance to next stage, Fail = end exam immediately.</p>
            <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr 1fr', gap: 20 }}>
              <div>
                <label style={{ display: 'block', marginBottom: 8, fontWeight: 600 }}>Supporting Basic Pass %</label>
                <input 
                  type="number" min="0" max="100"
                  value={thresholds.supporting_basic_pass}
                  onChange={(e) => setThresholds({...thresholds, supporting_basic_pass: Number(e.target.value)})}
                  style={{ width: '100%', padding: 10, borderRadius: 4, border: '1px solid #ccc', boxSizing: 'border-box' }}
                />
              </div>
              <div>
                <label style={{ display: 'block', marginBottom: 8, fontWeight: 600 }}>Supporting Int. Pass %</label>
                <input 
                  type="number" min="0" max="100"
                  value={thresholds.supporting_intermediate_pass}
                  onChange={(e) => setThresholds({...thresholds, supporting_intermediate_pass: Number(e.target.value)})}
                  style={{ width: '100%', padding: 10, borderRadius: 4, border: '1px solid #ccc', boxSizing: 'border-box' }}
                />
              </div>
              <div>
                <label style={{ display: 'block', marginBottom: 8, fontWeight: 600 }}>Supporting Adv. Pass %</label>
                <input 
                  type="number" min="0" max="100"
                  value={thresholds.supporting_advanced_pass}
                  onChange={(e) => setThresholds({...thresholds, supporting_advanced_pass: Number(e.target.value)})}
                  style={{ width: '100%', padding: 10, borderRadius: 4, border: '1px solid #ccc', boxSizing: 'border-box' }}
                />
              </div>
            </div>
          </div>

          <div style={{ display: 'flex', gap: 10 }}>
            <button 
              onClick={() => setCurrentStep(2)}
              style={{ padding: '12px 24px', background: '#6c757d', color: '#fff', border: 'none', borderRadius: 6, cursor: 'pointer' }}
            >
              ← Back
            </button>
            <button 
              onClick={handleCreateExam}
              disabled={loading}
              style={{ 
                padding: '12px 24px', 
                background: loading ? '#ccc' : '#28a745', 
                color: '#fff', 
                border: 'none', 
                borderRadius: 6, 
                cursor: loading ? 'not-allowed' : 'pointer',
                fontWeight: 600
              }}
            >
              {loading ? 'Creating...' : '✅ Create Exam'}
            </button>
          </div>
        </div>
      )}
    </div>
  );
}